import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";

import { createHighCourt, updateHighCourt } from "../../../../api/courtApis";
import SearchableSelect from "../../../../components/SearchableSelect";
import highCourtOptions from "../../../../state/benches.json";
import { indianStateList } from "../../../../utils/indianStateOptions";
import type { HighCourtRow } from "./types";
import {
  FormField,
  InfoNotice,
  inputClassName,
  Modal,
  ModalActions,
  TextInput,
  toDateInputValue,
} from "./ModalShell";
import DateInput from "react-calendar-ui-kit";
import { getTodayDate } from "../../../../utils/Date";
import { useFormDirty } from "../../../../hooks/useFormDirty";

interface AddUpdateHighCourtProps {
  court?: HighCourtRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const normalizeValue = (value?: string | number) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toHighCourtDateInputValue = (value?: string) => {
  if (!value) return "";

  const [day, month, year] = value.split("-");
  if (day && month && year) return `${year}-${month}-${day}`;

  return toDateInputValue(value);
};

export const AddUpdateHighCourt = ({
  court,
  onClose,
  onSaved,
}: AddUpdateHighCourtProps) => {
  const { data: highCourtResponse } = useSWR("/api/court/get-highcourt");
  const existingHighCourts = (highCourtResponse?.data || []) as HighCourtRow[];

  const initialForm = useMemo(() => ({
    highCourtName: court?.highCourtName || "",
    stateName: court?.stateName || "",
    stateCode: court?.stateCode || "",
    established: toDateInputValue(court?.established),
    capitalCity: court?.capitalCity || "",
    population: court?.population || "",
    status: court?.status || "active",
  }), []);
  const [form, setForm] = useState(initialForm);
  const isDirty = useFormDirty(initialForm, form);
  const [isSaving, setIsSaving] = useState(false);

  const notExistingHighCourts = highCourtOptions?.filter(
    (option) =>
      !existingHighCourts.some(
        (existing) => existing.highCourtName === option.highCourt,
      ),
  );

  const courtSelectOptions = (notExistingHighCourts as any[])?.map((item) => ({
    label: item.highCourt,
    value: item.highCourt,
  }));

  const existingCourtStateNames = highCourtOptions
    .filter((option) =>
      existingHighCourts.some((court) =>
        option.states_covered?.includes(court.stateName || ""),
      ),
    )
    .flatMap((option) => option.states_covered || []);

  const notExistingStateNames = indianStateList.filter(
    (state) => !existingCourtStateNames.includes(state.state),
  );

  const stateSelectOptions = (notExistingStateNames as any[])?.map((item) => ({
    label: item.state,
    value: item.state,
  }));

  const updateStateFromName = (stateName: string) => {
    if (!stateName) {
      setForm((prev) => ({
        ...prev,
        stateName: "",
        stateCode: "",
        capitalCity: "",
        population: "",
      }));
      return;
    }
    const state = indianStateList.find((item) => item.state === stateName);
    const highcourtForState = (highCourtOptions as any[])?.find((item) =>
      item.states_covered?.includes(stateName),
    );
    setForm((prev) => ({
      ...prev,
      highCourtName: highcourtForState?.high_court || prev.highCourtName,
      stateName,
      stateCode: state?.code || prev.stateCode,
      capitalCity: state?.capital || prev.capitalCity,
      population: state?.population || prev.population,
      established:
        toHighCourtDateInputValue(highcourtForState?.established) ||
        prev.established,
    }));
  };

  const handleSave = async () => {
    if (court && !isDirty) {
      toast.info("No changes detected");
      return;
    }
    if (
      !form.highCourtName ||
      !form.stateName ||
      !form.stateCode ||
      !form.established ||
      !form.capitalCity
    ) {
      toast.error("Fill all required high court fields");
      return;
    }

    const duplicateCourt = existingHighCourts.find((item) => {
      if (court?._id && item._id === court._id) return false;

      return (
        normalizeValue(item.highCourtName) ===
        normalizeValue(form.highCourtName) ||
        normalizeValue(item.stateName) === normalizeValue(form.stateName) ||
        normalizeValue(item.stateCode) === normalizeValue(form.stateCode)
      );
    });

    if (duplicateCourt) {
      let duplicateField = "State";

      if (
        normalizeValue(duplicateCourt.highCourtName) ===
        normalizeValue(form.highCourtName)
      ) {
        duplicateField = "High court name";
      } else if (
        normalizeValue(duplicateCourt.stateCode) ===
        normalizeValue(form.stateCode)
      ) {
        duplicateField = "Court code";
      }

      toast.error(`${duplicateField} already exists`,);
      return;
    }

    try {
      let res;
      setIsSaving(true);
      if (court?._id) {
        res = await updateHighCourt({
          _id: court._id,
          ...form,
          stateCode: form.stateCode.toUpperCase(),
        });
      } else {
        res = await createHighCourt({
          ...form,
          stateCode: form.stateCode.toUpperCase(),
        });
      }

      if (res?.data?.status) {
        toast.success(res.data.message || "High court saved");
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save high court");
      }
    } catch (error: any) {
      console.error("Error saving high court:", error);
      toast.error(error?.message || "Failed to save high court");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={court ? "Edit High Court" : "Add New High Court"}
      subtitle="Court Settings - Courts - High Courts"
      onClose={onClose}
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField label="High Court Name" required>
          <SearchableSelect
            options={courtSelectOptions}
            value={
              form.highCourtName
                ? { label: form.highCourtName, value: form.highCourtName }
                : null
            }
            onChange={(option: any) => {
              const selectedHighCourt = (highCourtOptions as any[])?.find(
                (item) => item.highCourt === option?.value,
              );
              const getStateName = selectedHighCourt?.states_covered;

              setForm((prev) => ({
                ...prev,
                highCourtName: option?.value || "",
                established: toHighCourtDateInputValue(
                  selectedHighCourt?.established,
                ),
              }));
              updateStateFromName(
                getStateName?.length > 0 ? getStateName[0] : "",
              );
            }}
            placeholder="e.g. Bombay High Court"
          />
        </FormField>
        <FormField label="State" required>
          <SearchableSelect
            options={stateSelectOptions}
            value={
              form.stateName
                ? { label: form.stateName, value: form.stateName }
                : null
            }
            onChange={(option: any) => updateStateFromName(option?.value || "")}
            placeholder="Select state"
            disabled
          />
        </FormField>
        <TextInput
          label="Court Code"
          value={form.stateCode}
          onChange={(value) =>
            setForm({ ...form, stateCode: value.slice(0, 2).toUpperCase() })
          }
          placeholder="E.G. MH"
          disabled
        />
        <TextInput
          label="Capital City"
          value={form.capitalCity}
          onChange={(value) => setForm({ ...form, capitalCity: value })}
          placeholder="e.g. Mumbai"
          disabled
        />
        <div>
          <label
            htmlFor="established"
            className="text-sm font-medium mb-2 block text-slate-700"
          >
            Established Date
          </label>
          <DateInput
            value={form.established}
            onChange={(value) => setForm({ ...form, established: value })}
            max={getTodayDate()}
            placeholder="Select established date"
            disabled
          />
        </div>
        <FormField label="Status" required>
          <select
            value={form.status}
            onChange={(event) =>
              setForm({ ...form, status: event.target.value })
            }
            className={inputClassName}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
      </div>
      <InfoNotice tone="blue">
        After adding a high court, you can navigate into it to add benches and
        configure court halls.
      </InfoNotice>
      <ModalActions
        label={court ? "Update High Court" : "Save High Court"}
        isSaving={isSaving}
        onCancel={onClose}
        onSave={handleSave}
        disabled={!!court && !isDirty}
      />
    </Modal>
  );
};
